import type { KeyValue, WrapperResponse } from '../../../model/kartera-api-model'
import APIInvoke from '../../../utils/APIInvoke'
import { fileTimestamp, isoYearsAgo, todayIso } from '../../../utils/dates'
import { compactAmount, formatAmount } from '../../../utils/numbers'

export { errorMessage, formatCount, labelize, money, pageWindow, personName, RamoMark } from '../../customers/common/customer-common'
export { formatSlashDate, formatSlashDateTime } from '../../../utils/dates'
export { compactAmount, formatAmount }

/** Tamaño que pide todos los registros del rango para calcular los acumulados en el navegador. */
export const ALL_ROWS = 999999
export const PAGE_SIZES = [20, 50, 100]

export const BREAKDOWN_COLORS = ['#4B44D8', '#14A79F', '#FF7A3D', '#EC3B6C', '#8B3FC9', '#3E6BE6', '#0B7A4F', '#F2B705', '#6E7385', '#B0204F']

export type BreakdownDimension = 'ramo' | 'status' | 'aseguradora'

export const BREAKDOWN_TABS: Array<{ id: BreakdownDimension; label: string }> = [
    { id: 'ramo', label: 'Ramos' },
    { id: 'status', label: 'Estatus' },
    { id: 'aseguradora', label: 'Aseguradoras' },
]

export interface BreakdownItem {
    name: string
    count: number
    sum: number
    countShare: number
    sumShare: number
    color: string
}

export interface ReportColumn<R> {
    key: string
    label: string
    align?: 'right'
    minWidth?: number
    csv: (row: R) => string | number
}

export const defaultRange = () => ({ from: isoYearsAgo(1), to: todayIso() })

/** Agrupa por la llave dada; ordena de mayor a menor importe. */
export const breakdown = <T>(items: T[], keyOf: (item: T) => string, amountOf: (item: T) => number): BreakdownItem[] => {
    const groups = new Map<string, { count: number; sum: number }>()
    items.forEach((item) => {
        const key = keyOf(item) || 'Sin dato'
        const group = groups.get(key) || { count: 0, sum: 0 }
        group.count += 1
        group.sum += amountOf(item) || 0
        groups.set(key, group)
    })
    const totalCount = items.length || 1
    const totalSum = [...groups.values()].reduce((acc, group) => acc + group.sum, 0) || 1
    return [...groups.entries()]
        .map(([name, group]) => ({ name, ...group }))
        .sort((left, right) => right.sum - left.sum || right.count - left.count)
        .map((group, index) => ({
            ...group,
            countShare: group.count / totalCount,
            sumShare: group.sum / totalSum,
            color: BREAKDOWN_COLORS[index % BREAKDOWN_COLORS.length],
        }))
}

/** Total con la moneda si todas las filas comparten una; si no, solo el número. */
export const totalLabel = <T>(items: T[], amountOf: (item: T) => number, currencyOf: (item: T) => string | null | undefined) => {
    const sum = items.reduce((acc, item) => acc + (amountOf(item) || 0), 0)
    const currencies = new Set(items.map((item) => currencyOf(item) || 'MXN'))
    if (currencies.size <= 1) return { value: `$${formatAmount(sum)}`, hint: [...currencies][0] || 'MXN' }
    return { value: `$${formatAmount(sum)}`, hint: `${currencies.size} monedas sin convertir` }
}

export const loadCatalog = async (path: string): Promise<KeyValue[]> => {
    const payload: WrapperResponse<KeyValue[]> | null = await (await APIInvoke.invokeGET(path)).json()
    return payload?.ok ? payload.body || [] : []
}

export const catalogLabel = (catalog: KeyValue[], key?: string | null) =>
    catalog.find((item) => item.key === key)?.value || key || '—'

const csvCell = (value: string | number) => {
    const text = String(value ?? '')
    return /[",\n;]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

/** Descarga un CSV con BOM para que Excel respete acentos. */
export const downloadCsv = <R>(name: string, columns: ReportColumn<R>[], rows: R[]) => {
    const lines = [
        columns.map((column) => csvCell(column.label)).join(','),
        ...rows.map((row) => columns.map((column) => csvCell(column.csv(row))).join(',')),
    ]
    const blob = new Blob([`\uFEFF${lines.join('\n')}`], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${name}-${fileTimestamp()}.csv`
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
}

const normalize = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()

export const matchesSearch = (query: string, ...values: Array<string | null | undefined>) => {
    const needle = normalize(query.trim())
    if (!needle) return true
    return normalize(values.filter(Boolean).join(' ')).includes(needle)
}

export const paginate = <T>(rows: T[], page: number, pageSize: number) => {
    const totalPages = Math.max(1, Math.ceil(rows.length / pageSize))
    const current = Math.min(page, totalPages)
    const start = (current - 1) * pageSize
    return {
        page: current,
        totalPages,
        rows: rows.slice(start, start + pageSize),
        rangeFrom: rows.length === 0 ? 0 : start + 1,
        rangeTo: rows.length === 0 ? 0 : Math.min(start + pageSize, rows.length),
    }
}
